import { useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function ProjectCard({ title, tag, images, span, position, testId }) {
  const [i, setI] = useState(0);
  const hasMultiple = images.length > 1;
  const touchStart = useRef(0);

  const next = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setI((p) => (p + 1) % images.length);
  };
  const prev = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setI((p) => (p - 1 + images.length) % images.length);
  };

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (!hasMultiple) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (diff > 50) next();
    else if (diff < -50) prev();
  };

  return (
    <div
      data-testid={testId}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`relative overflow-hidden rounded-2xl group ${span || "md:col-span-4"}`}
    >
      {images.map((img, idx) => (
        <img
          key={idx}
          src={img}
          alt={`${title} — ${tag}`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${idx === i ? "opacity-100" : "opacity-0"} group-hover:scale-105 transition-transform`}
          style={position ? { objectPosition: position } : undefined}
        />
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />

      {hasMultiple && (
        <>
          <button
            onClick={prev}
            data-testid={`${testId}-prev`}
            aria-label="Previous image"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-all duration-200 z-20"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={next}
            data-testid={`${testId}-next`}
            aria-label="Next image"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-all duration-200 z-20"
          >
            <ChevronRight size={18} />
          </button>

          <div className="absolute top-4 right-4 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/45 backdrop-blur-md text-white text-[10px] font-mono tracking-wider">
            <span>{String(i + 1).padStart(2, "0")}</span>
            <span className="opacity-50">/</span>
            <span className="opacity-80">{String(images.length).padStart(2, "0")}</span>
          </div>

          <div className="absolute top-4 left-4 z-10 flex gap-1.5">
            {images.map((_, idx) => (
              <button
                key={idx}
                data-testid={`${testId}-dot-${idx}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setI(idx);
                }}
                aria-label={`Go to image ${idx + 1}`}
                className={`h-1 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-white" : "w-3 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
        </>
      )}

      <div className="absolute inset-0 flex flex-col justify-end p-6 pointer-events-none">
        <div className="eyebrow text-white/80">{tag}</div>
        <div className="font-display text-2xl text-white mt-1">{title}</div>
      </div>
    </div>
  );
}
